/**
 * Die Fahrzeuge.
 *
 * **Keine Farben.** Erkannt wird am Kennzeichen — das ist eine der frühen
 * Entscheidungen und sie gilt auch hier: Sieben Fahrzeuge, aber nur fünf sicher
 * unterscheidbare Farbtöne, und es sollen mehr werden dürfen. Farbe bedeutet
 * in dieser Lösung immer und nur Status.
 *
 * Der Kurzname wird von der Datenbank berechnet (kürzeste eindeutige Stufe des
 * Kennzeichens) und ist deshalb hier nur Anzeige. Ihn von Hand zu setzen wäre
 * eine zweite Wahrheit, die beim nächsten neuen Fahrzeug falsch wird.
 *
 * Stillgelegte Fahrzeuge bleiben in der Liste stehen, nur zurückgenommen. Sie
 * auszublenden hieße, dass niemand sie wiederfindet, um sie zurückzuholen —
 * und ein verkauftes Fahrzeug taucht in alten Fahrten trotzdem auf.
 */

import { useMemo, useState } from 'react';
import { Seitenkopf } from '../Rahmen';
import {
  Fehlerkasten,
  Karte,
  Knopf,
  Marker,
  Suchleiste,
  Tabellenkarte,
  Textfeld,
} from '../gestaltung/bausteine';
import { useAbfrage } from '../daten/abfrage';
import { Speicherfehler } from '../daten/speichern';
import { alsKlarfehler, type Klarfehler } from '../daten/fehler';
import { passt } from '../daten/textvergleich';
import { Detailtafel, type Detailfeld } from '../gestaltung/Detailtafel';
import {
  fahrzeugAendern,
  fahrzeugAnlegen,
  fahrzeugeLaden,
  type Fahrzeug,
} from '../daten/stammdaten';
import { useFormularOffen, useEntwurfsfeld } from '../daten/formularEntwurf';

const FAHRZEUGFELDER: Detailfeld[] = [
  { schluessel: 'kennzeichen', marke: 'Kennzeichen' },
  { schluessel: 'bezeichnung', marke: 'Bezeichnung' },
  { schluessel: 'typ', marke: 'Typ' },
  {
    schluessel: 'kurzname',
    marke: 'Kurzname',
    schreibgeschuetzt: true,
    hinweis: 'Berechnet — kürzeste Stufe des Kennzeichens, die noch eindeutig ist.',
  },
  { schluessel: 'notiz', marke: 'Notiz' },
];

export function FahrzeugeSeite() {
  const liste = useAbfrage(fahrzeugeLaden, []);
  const [suche, setSuche] = useState('');
  // Offen bleibt offen — auch über Seitenwechsel und Neuladen (HJ-025).
  const [formularOffen, setFormularOffen] = useFormularOffen('fahrzeug-neu');
  const [fehler, setFehler] = useState<Klarfehler | null>(null);
  const [beschaeftigt, setBeschaeftigt] = useState<string | null>(null);
  const [offen, setOffen] = useState<Fahrzeug | null>(null);

  const gefiltert = useMemo(() => {
    const alle = liste.daten ?? [];
    if (!suche.trim()) return alle;
    return alle.filter(
      (f) =>
        passt(f.kennzeichen, suche) ||
        passt(f.bezeichnung ?? '', suche) ||
        passt(f.typ ?? '', suche),
    );
  }, [liste.daten, suche]);

  async function stilllegen(fahrzeug: Fahrzeug) {
    setFehler(null);
    setBeschaeftigt(fahrzeug.id);
    try {
      await fahrzeugAendern(fahrzeug, { aktiv: !fahrzeug.aktiv });
      liste.auffrischen();
    } catch (problem) {
      setFehler(
        problem instanceof Speicherfehler ? problem.klar : alsKlarfehler(problem),
      );
    } finally {
      setBeschaeftigt(null);
    }
  }

  return (
    <>
      <Seitenkopf
        titel="Fahrzeuge"
        erklaerung="Der Kurzname wird automatisch aus dem Kennzeichen gebildet."
        werkzeuge={
          <Knopf art="haupt" onClick={() => setFormularOffen(!formularOffen)}>
            {formularOffen ? 'Abbrechen' : 'Fahrzeug anlegen'}
          </Knopf>
        }
      />

      {fehler && (
        <div style={{ marginBottom: 16 }}>
          <Fehlerkasten fehler={fehler} />
        </div>
      )}

      {offen && (
        <div style={{ marginBottom: 16 }}>
          <Detailtafel
            key={offen.id}
            titel={offen.kennzeichen}
            felder={FAHRZEUGFELDER}
            laeuft={beschaeftigt === offen.id}
            werte={Object.fromEntries(
              FAHRZEUGFELDER.map((f) => [
                f.schluessel,
                String((offen as unknown as Record<string, unknown>)[f.schluessel] ?? ''),
              ]),
            )}
            schliessen={() => setOffen(null)}
            speichern={(geaendert) => {
              setFehler(null);
              setBeschaeftigt(offen.id);
              const werte: Record<string, unknown> = {};
              for (const [schluessel, wert] of Object.entries(geaendert)) {
                if (schluessel === 'kurzname') continue;
                werte[schluessel] =
                  wert.trim() === ''
                    ? null
                    : schluessel === 'kennzeichen'
                      ? wert.trim().toUpperCase()
                      : wert.trim();
              }
              void fahrzeugAendern(offen, werte as Partial<Fahrzeug>)
                .then(() => {
                  setOffen(null);
                  liste.auffrischen();
                })
                .catch((problem) =>
                  setFehler(
                    problem instanceof Speicherfehler
                      ? problem.klar
                      : alsKlarfehler(problem),
                  ),
                )
                .finally(() => setBeschaeftigt(null));
            }}
          />
        </div>
      )}

      {formularOffen && (
        <div style={{ marginBottom: 16 }}>
          <AnlegenFormular
            fertig={() => {
              setFormularOffen(false);
              liste.auffrischen();
            }}
          />
        </div>
      )}

      <Tabellenkarte
        titel="Alle Fahrzeuge"
        werkzeuge={
          <Suchleiste
            wert={suche}
            setzen={setSuche}
            platzhalter="Kennzeichen oder Bezeichnung"
            anzahl={`${gefiltert.length} von ${(liste.daten ?? []).length}`}
          />
        }
        laedt={liste.laedt}
        fehler={liste.fehler}
        nochmal={liste.auffrischen}
        eintraege={gefiltert}
        spalten={[
          { text: 'Kennzeichen' },
          { text: 'Kurzname' },
          { text: 'Bezeichnung' },
          { text: 'Typ' },
          { text: 'Status' },
          { text: '', rechts: true },
        ]}
        leer={
          suche
            ? 'Kein Fahrzeug passt zu dieser Suche.'
            : 'Noch kein Fahrzeug angelegt.'
        }
        zeile={(fahrzeug) => (
          <tr
            key={fahrzeug.id}
            className={fahrzeug.aktiv ? undefined : 'stillgelegt'}
            aria-current={offen?.id === fahrzeug.id ? 'true' : undefined}
          >
            <td>
              <button className="zeilenlink" onClick={() => setOffen(fahrzeug)}>
                {fahrzeug.kennzeichen}
              </button>
            </td>
            <td className="code">{fahrzeug.kurzname ?? '—'}</td>
            <td>{fahrzeug.bezeichnung ?? '—'}</td>
            <td>{fahrzeug.typ ?? '—'}</td>
            <td>
              {fahrzeug.aktiv ? (
                <Marker art="gut">Im Einsatz</Marker>
              ) : (
                <Marker art="neutral">Stillgelegt</Marker>
              )}
            </td>
            <td className="tabelle__rechts">
              <Knopf
                art="still"
                klein
                disabled={beschaeftigt === fahrzeug.id}
                onClick={() => void stilllegen(fahrzeug)}
                title={
                  fahrzeug.aktiv
                    ? 'Nimmt das Fahrzeug aus den Auswahllisten. Alte Fahrten bleiben unberührt.'
                    : undefined
                }
              >
                {fahrzeug.aktiv ? 'Stilllegen' : 'Zurückholen'}
              </Knopf>
            </td>
          </tr>
        )}
      />
    </>
  );
}

function AnlegenFormular(eigenschaften: { fertig: () => void }) {
  const [kennzeichen, setKennzeichen] = useEntwurfsfeld('fahrzeug-neu', 'kennzeichen', '');
  const [bezeichnung, setBezeichnung] = useEntwurfsfeld('fahrzeug-neu', 'bezeichnung', '');
  const [typ, setTyp] = useEntwurfsfeld('fahrzeug-neu', 'typ', '');
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<Klarfehler | null>(null);

  async function speichern() {
    if (!kennzeichen.trim() || laeuft) return;
    setLaeuft(true);
    setFehler(null);
    try {
      await fahrzeugAnlegen({ kennzeichen, bezeichnung, typ });
      eigenschaften.fertig();
    } catch (problem) {
      setFehler(
        problem instanceof Speicherfehler ? problem.klar : alsKlarfehler(problem),
      );
      setLaeuft(false);
    }
  }

  return (
    <Karte titel="Neues Fahrzeug">
      <div className="stapel">
        {fehler && <Fehlerkasten fehler={fehler} />}
        <div className="feldreihe">
          <Textfeld
            marke="Kennzeichen"
            wert={kennzeichen}
            setzen={setKennzeichen}
            autoFocus
            platzhalter="BC HJ 1234"
            hinweis="Wird groß geschrieben gespeichert. Der Kurzname ergibt sich daraus."
          />
          <Textfeld
            marke="Bezeichnung"
            wert={bezeichnung}
            setzen={setBezeichnung}
            hinweis="Optional, z. B. „Vierachser“."
          />
          <Textfeld
            marke="Typ"
            wert={typ}
            setzen={setTyp}
            hinweis="Optional, z. B. „Sattelkipper“."
          />
        </div>
        <div className="reihe reihe--rechts">
          <Knopf art="still" onClick={eigenschaften.fertig}>
            Abbrechen
          </Knopf>
          <Knopf
            art="haupt"
            disabled={!kennzeichen.trim() || laeuft}
            onClick={() => void speichern()}
          >
            {laeuft ? 'Wird angelegt …' : 'Anlegen'}
          </Knopf>
        </div>
      </div>
    </Karte>
  );
}
